import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportingAuthoritiesQueryOptions } from '@/features/users/api/users.api';
import { OptionSelect, type SelectOption } from './OptionSelect';

interface ReportingAuthoritySelectProps {
  userTypeId: number;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  currentName?: string | undefined;
}

export function ReportingAuthoritySelect({
  userTypeId,
  value,
  onChange,
  placeholder = 'Select reporting manager',
  disabled,
  id,
  className,
  currentName,
}: ReportingAuthoritySelectProps) {
  const { data, isLoading } = useQuery(reportingAuthoritiesQueryOptions(userTypeId));

  const options = useMemo<SelectOption[] | undefined>(() => {
    if (data === undefined) return undefined;

    const fetchedOptions = data.map((user) => ({
      id: user.id,
      name: `${user.fullName} (${user.hrEmpId})`,
    }));

    if (value === undefined || currentName === undefined) return fetchedOptions;

    const valueAlreadyInList = fetchedOptions.some((option) => option.id === value);
    if (valueAlreadyInList) return fetchedOptions;

    const currentSelection: SelectOption = { id: value, name: currentName };
    return [currentSelection, ...fetchedOptions];
  }, [data, value, currentName]);

  return (
    <OptionSelect
      options={options}
      isLoading={isLoading}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled ?? false}
      id={id}
      className={className ?? ''}
    />
  );
}
